// @flow

import type { Action } from './store.type';
import type {
  SearchTermsType,
  SearchesWithoutClickType,
  TimeSeriesType,
  ConversionRateType,
  AppImpactType,
  AppImpactSpecific,
  ChartSourceType,
  TopProductsOnSearchType
} from 'src/models/analytics';

type AnalyticFailure = {
  error: any
};

type TimeSeriesSuccessType = {
  data: TimeSeriesType
};

export const CHANGE_VALUE = 'ANALYTICS-V2/CHANGE_VALUE';
type CHANGE_VALUE_TYPE = 'ANALYTICS-V2/CHANGE_VALUE';

type ChangeValueAction = Action<CHANGE_VALUE_TYPE, { name: string, value: any }>;

export const SORT_DATA = 'ANALYTICS-V2/SORT_DATA';
type SORT_DATA_TYPE = 'ANALYTICS-V2/SORT_DATA';

type SortDataAction = Action<
  SORT_DATA_TYPE,
  { name: string, field: string, direction: string, fieldInsensitive: string }
>;

// Total search revenue
export const GET_TOTAL_SEARCH_REVENUE_REQUEST = 'ANALYTICS-V2/GET_TOTAL_SEARCH_REVENUE_REQUEST';
type GET_TOTAL_SEARCH_REVENUE_REQUEST_TYPE = 'ANALYTICS-V2/GET_TOTAL_SEARCH_REVENUE_REQUEST';

export const GET_TOTAL_SEARCH_REVENUE_SUCCESS = 'ANALYTICS-V2/GET_TOTAL_SEARCH_REVENUE_SUCCESS';
type GET_TOTAL_SEARCH_REVENUE_SUCCESS_TYPE = 'ANALYTICS-V2/GET_TOTAL_SEARCH_REVENUE_SUCCESS';

export const GET_TOTAL_SEARCH_REVENUE_FAILURE = 'ANALYTICS-V2/GET_TOTAL_SEARCH_REVENUE_FAILURE';
type GET_TOTAL_SEARCH_REVENUE_FAILURE_TYPE = 'ANALYTICS-V2/GET_TOTAL_SEARCH_REVENUE_FAILURE';

type GetTotalSearchRevenueRequestAction = Action<GET_TOTAL_SEARCH_REVENUE_REQUEST_TYPE, null>;

type GetTotalSearchRevenueSuccessAction = Action<GET_TOTAL_SEARCH_REVENUE_SUCCESS_TYPE, any>;

type GetTotalSearchRevenueFailureAction = Action<
  GET_TOTAL_SEARCH_REVENUE_FAILURE_TYPE,
  AnalyticFailure
>;

// Top search terms
export const GET_TOP_SEARCH_TERMS_REQUEST = 'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_REQUEST';
type GET_TOP_SEARCH_TERMS_REQUEST_TYPE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_REQUEST';

export const GET_TOP_SEARCH_TERMS_SUCCESS = 'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_SUCCESS';
type GET_TOP_SEARCH_TERMS_SUCCESS_TYPE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_SUCCESS';

export const GET_TOP_SEARCH_TERMS_FAILURE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_FAILURE';
type GET_TOP_SEARCH_TERMS_FAILURE_TYPE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_FAILURE';

type GetTopSearchTermsSuccess = {
  data: SearchTermsType
};

type GetTopSearchTermsRequestAction = Action<GET_TOP_SEARCH_TERMS_REQUEST_TYPE, null>;

type GetTopSearchTermsSuccessAction = Action<
  GET_TOP_SEARCH_TERMS_SUCCESS_TYPE,
  GetTopSearchTermsSuccess
>;

type GetTopSearchTermsFailureAction = Action<GET_TOP_SEARCH_TERMS_FAILURE_TYPE, AnalyticFailure>;

// Top search terms
export const GET_TOP_SEARCH_TERM_DETAIL_REQUEST = 'ANALYTICS-V2/GET_TOP_SEARCH_TERM_DETAIL_REQUEST';
type GET_TOP_SEARCH_TERM_DETAIL_REQUEST_TYPE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERM_DETAIL_REQUEST';

export const GET_TOP_SEARCH_TERM_DETAIL_SUCCESS = 'ANALYTICS-V2/GET_TOP_SEARCH_TERM_DETAIL_SUCCESS';
type GET_TOP_SEARCH_TERM_DETAIL_SUCCESS_TYPE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERM_DETAIL_SUCCESS';

export const GET_TOP_SEARCH_TERM_DETAIL_FAILURE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERM_DETAIL_FAILURE';
type GET_TOP_SEARCH_TERM_DETAIL_FAILURE_TYPE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERM_DETAIL_FAILURE';

type GetTopSearchTermDetailSuccess = {
  data: SearchTermsType
};

type GetTopSearchTermDetailRequestAction = Action<GET_TOP_SEARCH_TERM_DETAIL_REQUEST_TYPE, null>;

type GetTopSearchTermDetailSuccessAction = Action<
  GET_TOP_SEARCH_TERM_DETAIL_SUCCESS_TYPE,
  GetTopSearchTermDetailSuccess
>;

type GetTopSearchTermDetailFailureAction = Action<
  GET_TOP_SEARCH_TERM_DETAIL_FAILURE_TYPE,
  AnalyticFailure
>;

// Product performance
export const GET_TOP_SEARCH_PRODUCT_PERFORMANCE_REQUEST =
  'ANALYTICS-V2/GET_TOP_SEARCH_PRODUCT_PERFORMANCE_REQUEST';
type GET_TOP_SEARCH_PRODUCT_PERFORMANCE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_TOP_SEARCH_PRODUCT_PERFORMANCE_REQUEST';

export const GET_TOP_SEARCH_PRODUCT_PERFORMANCE_SUCCESS =
  'ANALYTICS-V2/GET_TOP_SEARCH_PRODUCT_PERFORMANCE_SUCCESS';
type GET_TOP_SEARCH_PRODUCT_PERFORMANCE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_TOP_SEARCH_PRODUCT_PERFORMANCE_SUCCESS';

export const GET_TOP_SEARCH_PRODUCT_PERFORMANCE_FAILURE =
  'ANALYTICS-V2/GET_TOP_SEARCH_PRODUCT_PERFORMANCE_FAILURE';
type GET_TOP_SEARCH_PRODUCT_PERFORMANCE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_TOP_SEARCH_PRODUCT_PERFORMANCE_FAILURE';

type GetTopSearchProductPerformanceSuccess = {
  data: SearchTermsType
};

type GetTopSearchProductPerformanceRequestAction = Action<
  GET_TOP_SEARCH_PRODUCT_PERFORMANCE_REQUEST_TYPE,
  { noResult: boolean }
>;

type GetTopSearchProductPerformanceSuccessAction = Action<
  GET_TOP_SEARCH_PRODUCT_PERFORMANCE_SUCCESS_TYPE,
  GetTopSearchProductPerformanceSuccess
>;

type GetTopSearchProductPerformanceFailureAction = Action<
  GET_TOP_SEARCH_PRODUCT_PERFORMANCE_FAILURE_TYPE,
  AnalyticFailure
>;

// Searches without clicks
export const GET_SEARCHES_WITHOUT_CLICK_REQUEST = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_REQUEST';
type GET_SEARCHES_WITHOUT_CLICK_REQUEST_TYPE = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_REQUEST';

export const GET_SEARCHES_WITHOUT_CLICK_SUCCESS = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_SUCCESS';
type GET_SEARCHES_WITHOUT_CLICK_SUCCESS_TYPE = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_SUCCESS';

export const GET_SEARCHES_WITHOUT_CLICK_FAILURE = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_FAILURE';
type GET_SEARCHES_WITHOUT_CLICK_FAILURE_TYPE = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_FAILURE';

type getSearchesWithoutClickAction = Action<GET_SEARCHES_WITHOUT_CLICK_REQUEST_TYPE, null>;
type getSearchesWithoutClickSuccessAction = Action<
  GET_SEARCHES_WITHOUT_CLICK_SUCCESS_TYPE,
  { data: SearchesWithoutClickType }
>;
type getSearchesWithoutClickFailureAction = Action<
  GET_SEARCHES_WITHOUT_CLICK_FAILURE_TYPE,
  { error: any }
>;

// Search terms without result
export const GET_SEARCH_TERMS_WITHOUT_RESULT = 'ANALYTICS-V2/GET_SEARCH_TERMS_WITHOUT_RESULT';
type GET_SEARCH_TERMS_WITHOUT_RESULT_TYPE = 'ANALYTICS-V2/GET_SEARCH_TERMS_WITHOUT_RESULT';

export const GET_SEARCH_TERMS_WITHOUT_RESULT_SUCCESS =
  'ANALYTICS-V2/GET_SEARCH_TERMS_WITHOUT_RESULT_SUCCESS';
type GET_SEARCH_TERMS_WITHOUT_RESULT_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_SEARCH_TERMS_WITHOUT_RESULT_SUCCESS';

export const GET_SEARCH_TERMS_WITHOUT_RESULT_FAILURE =
  'ANALYTICS-V2/GET_SEARCH_TERMS_WITHOUT_RESULT_FAILURE';
type GET_SEARCH_TERMS_WITHOUT_RESULT_FAILURE_TYPE =
  'ANALYTICS-V2/GET_SEARCH_TERMS_WITHOUT_RESULT_FAILURE';

type GetSearchTermsWithoutResultSuccess = {
  data: SearchTermType
};

type GetSearchTermsWithoutResultRequestAction = Action<GET_SEARCH_TERMS_WITHOUT_RESULT_TYPE, null>;

type GetSearchTermsWithoutResultSuccessAction = Action<
  GET_SEARCH_TERMS_WITHOUT_RESULT_SUCCESS_TYPE,
  GetSearchTermsWithoutResultSuccess
>;

type GetSearchTermsWithoutResultFailureAction = Action<
  GET_SEARCH_TERMS_WITHOUT_RESULT_FAILURE_TYPE,
  AnalyticFailure
>;

// Top search terms with click
export const GET_TOP_SEARCH_TERMS_WITH_CLICK = 'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_WITH_CLICK';
const GET_TOP_SEARCH_TERMS_WITH_CLICK_TYPE = 'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_WITH_CLICK';
export const GET_TOP_SEARCH_TERMS_WITH_CLICK_SUCCESS =
  'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_WITH_CLICK_SUCCESS';
type GET_TOP_SEARCH_TERMS_WITH_CLICK_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_WITH_CLICK_SUCCESS';
export const GET_TOP_SEARCH_TERMS_WITH_CLICK_FAILURE =
  'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_WITH_CLICK_FAILURE';
type GET_TOP_SEARCH_TERMS_WITH_CLICK_FAILURE_TYPE =
  'ANALYTICS-V2/GET_TOP_SEARCH_TERMS_WITH_CLICK_FAILURE';

type GetTopSearchTermsWithClickRequestAction = Action<GET_TOP_SEARCH_TERMS_WITH_CLICK_TYPE, null>;
type GetTopSearchTermsWithClickSuccessAction = Action<
  GET_TOP_SEARCH_TERMS_WITH_CLICK_SUCCESS_TYPE,
  GetTopSearchTermsSuccess
>;
type GetTopSearchTermsWithClickFailureAction = Action<
  GET_TOP_SEARCH_TERMS_WITH_CLICK_FAILURE_TYPE,
  AnalyticFailure
>;

// Searches without result rate
export const GET_SEARCHES_WITHOUT_RESULT_RATE = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_RESULT_RATE';
type GET_SEARCHES_WITHOUT_RESULT_RATE_TYPE = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_RESULT_RATE';

export const GET_SEARCHES_WITHOUT_RESULT_RATE_SUCCESS =
  'ANALYTICS-V2/GET_SEARCHES_WITHOUT_RESULT_RATE_SUCCESS';
type GET_SEARCHES_WITHOUT_RESULT_RATE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_SEARCHES_WITHOUT_RESULT_RATE_SUCCESS';

export const GET_SEARCHES_WITHOUT_RESULT_RATE_FAILURE =
  'ANALYTICS-V2/GET_SEARCHES_WITHOUT_RESULT_RATE_FAILURE';
type GET_SEARCHES_WITHOUT_RESULT_RATE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_SEARCHES_WITHOUT_RESULT_RATE_FAILURE';

type GetSearchesWithoutResultRateRequestAction = Action<
  GET_SEARCHES_WITHOUT_RESULT_RATE_TYPE,
  null
>;

type GetSearchesWithoutResultRateSuccessAction = Action<
  GET_SEARCHES_WITHOUT_RESULT_RATE_SUCCESS_TYPE,
  TimeSeriesSuccessType
>;

type GetSearchesWithoutResultRateFailureAction = Action<
  GET_SEARCHES_WITHOUT_RESULT_RATE_FAILURE_TYPE,
  AnalyticFailure
>;

// Searches click rate
export const GET_SEARCHES_CLICK_RATE = 'ANALYTICS-V2/GET_SEARCHES_CLICK_RATE';
type GET_SEARCHES_CLICK_RATE_TYPE = 'ANALYTICS-V2/GET_SEARCHES_CLICK_RATE';

export const GET_SEARCHES_CLICK_RATE_SUCCESS = 'ANALYTICS-V2/GET_SEARCHES_CLICK_RATE_SUCCESS';
type GET_SEARCHES_CLICK_RATE_SUCCESS_TYPE = 'ANALYTICS-V2/GET_SEARCHES_CLICK_RATE_SUCCESS';

export const GET_SEARCHES_CLICK_RATE_FAILURE = 'ANALYTICS-V2/GET_SEARCHES_CLICK_RATE_FAILURE';
type GET_SEARCHES_CLICK_RATE_FAILURE_TYPE = 'ANALYTICS-V2/GET_SEARCHES_CLICK_RATE_FAILURE';

type GetSearchesClickRateRequestAction = Action<GET_SEARCHES_CLICK_RATE_TYPE, null>;

type GetSearchesClickRateSuccessAction = Action<
  GET_SEARCHES_CLICK_RATE_SUCCESS_TYPE,
  TimeSeriesSuccessType
>;

type GetSearchesClickRateFailureAction = Action<
  GET_SEARCHES_CLICK_RATE_FAILURE_TYPE,
  AnalyticFailure
>;

// Searches without clicks rate
export const GET_SEARCHES_WITHOUT_CLICK_RATE = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_RATE';
type GET_SEARCHES_WITHOUT_CLICK_RATE_TYPE = 'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_RATE';

export const GET_SEARCHES_WITHOUT_CLICK_RATE_SUCCESS =
  'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_RATE_SUCCESS';
type GET_SEARCHES_WITHOUT_CLICK_RATE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_RATE_SUCCESS';

export const GET_SEARCHES_WITHOUT_CLICK_RATE_FAILURE =
  'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_RATE_FAILURE';
type GET_SEARCHES_WITHOUT_CLICK_RATE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_SEARCHES_WITHOUT_CLICK_RATE_FAILURE';

type GetSearchesWithoutClickRateRequestAction = Action<GET_SEARCHES_WITHOUT_CLICK_RATE_TYPE, null>;

type GetSearchesWithoutClickRateSuccessAction = Action<
  GET_SEARCHES_WITHOUT_CLICK_RATE_SUCCESS_TYPE,
  TimeSeriesSuccessType
>;

type GetSearchesWithoutClickRateFailureAction = Action<
  GET_SEARCHES_WITHOUT_CLICK_RATE_FAILURE_TYPE,
  AnalyticFailure
>;

// Conversion rate
export const GET_CONVERSION_RATE = 'ANALYTICS-V2/GET_CONVERSION_RATE';
type GET_CONVERSION_RATE_TYPE = 'ANALYTICS-V2/GET_CONVERSION_RATE';

export const GET_CONVERSION_RATE_SUCCESS = 'ANALYTICS-V2/GET_CONVERSION_RATE_SUCCESS';
type GET_CONVERSION_RATE_SUCCESS_TYPE = 'ANALYTICS-V2/GET_CONVERSION_RATE_SUCCESS';

export const GET_CONVERSION_RATE_FAILURE = 'ANALYTICS-V2/GET_CONVERSION_RATE_FAILURE';
type GET_CONVERSION_RATE_FAILURE_TYPE = 'ANALYTICS-V2/GET_CONVERSION_RATE_FAILURE';

type GetConversionRateSuccess = {
  data: ConversionRateType
};

type GetConversionRateRequestAction = Action<GET_CONVERSION_RATE_TYPE, null>;

type GetConversionRateSuccessAction = Action<
  GET_CONVERSION_RATE_SUCCESS_TYPE,
  GetConversionRateSuccess
>;

type GetConversionRateFailureAction = Action<GET_CONVERSION_RATE_FAILURE_TYPE, AnalyticFailure>;

// Get searches rate
export const GET_SEARCHES_RATE = 'ANALYTICS-V2/GET_SEARCHES_RATE';
type GET_SEARCHES_RATE_TYPE = 'ANALYTICS-V2/GET_SEARCHES_RATE';

export const GET_SEARCHES_RATE_SUCCESS = 'ANALYTICS-V2/GET_SEARCHES_RATE_SUCCESS';
type GET_SEARCHES_RATE_SUCCESS_TYPE = 'ANALYTICS-V2/GET_SEARCHES_RATE_SUCCESS';

export const GET_SEARCHES_RATE_FAILURE = 'ANALYTICS-V2/GET_SEARCHES_RATE_FAILURE';
type GET_SEARCHES_RATE_FAILURE_TYPE = 'ANALYTICS-V2/GET_SEARCHES_RATE_FAILURE';

type GetSearchesRateRequestAction = Action<GET_SEARCHES_RATE_TYPE, null>;

type GetSearchesRateSuccessAction = Action<GET_SEARCHES_RATE_SUCCESS_TYPE, TimeSeriesSuccessType>;

type GetSearchesRateFailureAction = Action<GET_SEARCHES_RATE_FAILURE_TYPE, AnalyticFailure>;

// Export data
export const EXPORT_DATA_REQUEST = 'ANALYTICS-V2/EXPORT_DATA_REQUEST';
type EXPORT_DATA_REQUEST_TYPE = 'ANALYTICS-V2/EXPORT_DATA_REQUEST';

export const EXPORT_DATA_SUCCESS = 'ANALYTICS-V2/EXPORT_DATA_SUCCESS';
type EXPORT_DATA_SUCCESS_TYPE = 'ANALYTICS-V2/EXPORT_DATA_SUCCESS';

export const EXPORT_DATA_FAILURE = 'ANALYTICS-V2/EXPORT_DATA_FAILURE';
type EXPORT_DATA_FAILURE_TYPE = 'ANALYTICS-V2/EXPORT_DATA_FAILURE';

type ExportDataRequestAction = Action<EXPORT_DATA_REQUEST_TYPE, null>;

type ExportDataSuccessAction = Action<EXPORT_DATA_SUCCESS_TYPE, { data: Object }>;

type ExportDataFailureAction = Action<EXPORT_DATA_FAILURE_TYPE, AnalyticFailure>;

// ----------------------------------------- App impact
type AppImpactResponseSuccess = {
  data: AppImpactType
};

// Total activity
export const GET_TOTAL_ACTIVITY = 'ANALYTICS-V2/GET_TOTAL_ACTIVITY';
type GET_TOTAL_ACTIVITY_TYPE = 'ANALYTICS-V2/GET_TOTAL_ACTIVITY';

export const GET_TOTAL_ACTIVITY_SUCCESS = 'ANALYTICS-V2/GET_TOTAL_ACTIVITY_SUCCESS';
type GET_TOTAL_ACTIVITY_SUCCESS_TYPE = 'ANALYTICS-V2/GET_TOTAL_ACTIVITY_SUCCESS';

export const GET_TOTAL_ACTIVITY_FAILURE = 'ANALYTICS-V2/GET_TOTAL_ACTIVITY_FAILURE';
type GET_TOTAL_ACTIVITY_FAILURE_TYPE = 'ANALYTICS-V2/GET_TOTAL_ACTIVITY_FAILURE';

type GetTotalActivityRequestAction = Action<GET_TOTAL_ACTIVITY_TYPE, null>;
type GetTotalActivitySuccessAction = Action<
  GET_TOTAL_ACTIVITY_SUCCESS_TYPE,
  AppImpactResponseSuccess
>;
type GetTotalActivityFailureAction = Action<GET_TOTAL_ACTIVITY_FAILURE_TYPE, AnalyticFailure>;

// Total revenue
export const GET_TOTAL_REVENUE = 'ANALYTICS-V2/GET_TOTAL_REVENUE';
type GET_TOTAL_REVENUE_TYPE = 'ANALYTICS-V2/GET_TOTAL_REVENUE';

export const GET_TOTAL_REVENUE_SUCCESS = 'ANALYTICS-V2/GET_TOTAL_REVENUE_SUCCESS';
type GET_TOTAL_REVENUE_SUCCESS_TYPE = 'ANALYTICS-V2/GET_TOTAL_REVENUE_SUCCESS';

export const GET_TOTAL_REVENUE_FAILURE = 'ANALYTICS-V2/GET_TOTAL_REVENUE_FAILURE';
type GET_TOTAL_REVENUE_FAILURE_TYPE = 'ANALYTICS-V2/GET_TOTAL_REVENUE_FAILURE';

type GetTotalRevenueRequestAction = Action<GET_TOTAL_REVENUE_TYPE, null>;
type GetTotalRevenueSuccessAction = Action<
  GET_TOTAL_REVENUE_SUCCESS_TYPE,
  AppImpactResponseSuccess
>;
type GetTotalRevenueFailureAction = Action<GET_TOTAL_REVENUE_FAILURE_TYPE, AnalyticFailure>;

// App impact Conversion rate
export const GET_APP_IMPACT_CONVERSION_RATE = 'ANALYTICS-V2/GET_APP_IMPACT_CONVERSION_RATE';
type GET_APP_IMPACT_CONVERSION_RATE_TYPE = 'ANALYTICS-V2/GET_APP_IMPACT_CONVERSION_RATE';

export const GET_APP_IMPACT_CONVERSION_RATE_SUCCESS =
  'ANALYTICS-V2/GET_APP_IMPACT_CONVERSION_RATE_SUCCESS';
type GET_APP_IMPACT_CONVERSION_RATE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_APP_IMPACT_CONVERSION_RATE_SUCCESS';

export const GET_APP_IMPACT_CONVERSION_RATE_FAILURE =
  'ANALYTICS-V2/GET_APP_IMPACT_CONVERSION_RATE_FAILURE';
type GET_APP_IMPACT_CONVERSION_RATE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_APP_IMPACT_CONVERSION_RATE_FAILURE';

type GetAppImpactConversionRateSuccess = {
  data: AppImpactConversionRateType
};

type GetAppImpactConversionRateRequestAction = Action<GET_APP_IMPACT_CONVERSION_RATE_TYPE, null>;

type GetAppImpactConversionRateSuccessAction = Action<
  GET_APP_IMPACT_CONVERSION_RATE_SUCCESS_TYPE,
  GetAppImpactConversionRateSuccess
>;
type GetAppImpactConversionRateFailureAction = Action<
  GET_APP_IMPACT_CONVERSION_RATE_FAILURE_TYPE,
  AnalyticFailure
>;

// Order count
export const GET_ORDER_COUNT = 'ANALYTICS-V2/GET_ORDER_COUNT';
type GET_ORDER_COUNT_TYPE = 'ANALYTICS-V2/GET_ORDER_COUNT';

export const GET_ORDER_COUNT_SUCCESS = 'ANALYTICS-V2/GET_ORDER_COUNT_SUCCESS';
type GET_ORDER_COUNT_SUCCESS_TYPE = 'ANALYTICS-V2/GET_ORDER_COUNT_SUCCESS';

export const GET_ORDER_COUNT_FAILURE = 'ANALYTICS-V2/GET_ORDER_COUNT_FAILURE';
type GET_ORDER_COUNT_FAILURE_TYPE = 'ANALYTICS-V2/GET_ORDER_COUNT_FAILURE';

type GetOrderCountRequestAction = Action<GET_ORDER_COUNT_TYPE, null>;
type GetOrderCountSuccessAction = Action<GET_ORDER_COUNT_SUCCESS_TYPE, AppImpactResponseSuccess>;
type GetOrderCountFailureAction = Action<GET_ORDER_COUNT_FAILURE_TYPE, AnalyticFailure>;

// Average order value
export const GET_AVERAGE_ORDER_VALUE = 'ANALYTICS-V2/GET_AVERAGE_ORDER_VALUE';
type GET_AVERAGE_ORDER_VALUE_TYPE = 'ANALYTICS-V2/GET_AVERAGE_ORDER_VALUE';

export const GET_AVERAGE_ORDER_VALUE_SUCCESS = 'ANALYTICS-V2/GET_AVERAGE_ORDER_VALUE_SUCCESS';
type GET_AVERAGE_ORDER_VALUE_SUCCESS_TYPE = 'ANALYTICS-V2/GET_AVERAGE_ORDER_VALUE_SUCCESS';

export const GET_AVERAGE_ORDER_VALUE_FAILURE = 'ANALYTICS-V2/GET_AVERAGE_ORDER_VALUE_FAILURE';
type GET_AVERAGE_ORDER_VALUE_FAILURE_TYPE = 'ANALYTICS-V2/GET_AVERAGE_ORDER_VALUE_FAILURE';

type GetAverageOrderValueRequestAction = Action<GET_AVERAGE_ORDER_VALUE_TYPE, null>;
type GetAverageOrderValueSuccessAction = Action<
  GET_AVERAGE_ORDER_VALUE_SUCCESS_TYPE,
  AppImpactResponseSuccess
>;
type GetAverageOrderValueFailureAction = Action<
  GET_AVERAGE_ORDER_VALUE_FAILURE_TYPE,
  AnalyticFailure
>;

// Revenue per visitor
export const GET_REVENUE_PER_VISITOR = 'ANALYTICS-V2/GET_REVENUE_PER_VISITOR';
type GET_REVENUE_PER_VISITOR_TYPE = 'ANALYTICS-V2/GET_REVENUE_PER_VISITOR';

export const GET_REVENUE_PER_VISITOR_SUCCESS = 'ANALYTICS-V2/GET_REVENUE_PER_VISITOR_SUCCESS';
type GET_REVENUE_PER_VISITOR_SUCCESS_TYPE = 'ANALYTICS-V2/GET_REVENUE_PER_VISITOR_SUCCESS';

export const GET_REVENUE_PER_VISITOR_FAILURE = 'ANALYTICS-V2/GET_REVENUE_PER_VISITOR_FAILURE';
type GET_REVENUE_PER_VISITOR_FAILURE_TYPE = 'ANALYTICS-V2/GET_REVENUE_PER_VISITOR_FAILURE';

type GetRevenuePerVisitorRequestAction = Action<GET_REVENUE_PER_VISITOR_TYPE, null>;
type GetRevenuePerVisitorSuccessAction = Action<
  GET_REVENUE_PER_VISITOR_SUCCESS_TYPE,
  AppImpactResponseSuccess
>;
type GetRevenuePerVisitorFailureAction = Action<
  GET_REVENUE_PER_VISITOR_FAILURE_TYPE,
  AnalyticFailure
>;

// ----------------------------------------- App impact
type CollectionAnalyticResponseSuccess = {
  data: AppImpactType
};

// Collection get collection conversion rate
export const GET_COLLECTION_CONVERSION_RATE_REQUEST =
  'ANALYTICS-V2/GET_COLLECTION_CONVERSION_RATE_REQUEST';
type GET_COLLECTION_CONVERSION_RATE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_CONVERSION_RATE_REQUEST';

export const GET_COLLECTION_CONVERSION_RATE_SUCCESS =
  'ANALYTICS-V2/GET_COLLECTION_CONVERSION_RATE_SUCCESS';
type GET_COLLECTION_CONVERSION_RATE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_CONVERSION_RATE_SUCCESS';

export const GET_COLLECTION_CONVERSION_RATE_FAILURE =
  'ANALYTICS-V2/GET_COLLECTION_CONVERSION_RATE_FAILURE';
type GET_COLLECTION_CONVERSION_RATE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_CONVERSION_RATE_FAILURE';

type GetCollectionConversionRateRequestAction = Action<
  GET_COLLECTION_CONVERSION_RATE_REQUEST_TYPE,
  null
>;
type GetCollectionConversionRateSuccessAction = Action<
  GET_COLLECTION_CONVERSION_RATE_SUCCESS_TYPE,
  CollectionAnalyticResponseSuccess
>;
type GetCollectionConversionRateFailureAction = Action<
  GET_COLLECTION_CONVERSION_RATE_FAILURE_TYPE,
  AnalyticFailure
>;

// Collection get top collection
export const GET_COLLECTION_TOP_REQUEST = 'ANALYTICS-V2/GET_COLLECTION_TOP_REQUEST';
type GET_COLLECTION_TOP_REQUEST_TYPE = 'ANALYTICS-V2/GET_COLLECTION_TOP_REQUEST';

export const GET_COLLECTION_TOP_SUCCESS = 'ANALYTICS-V2/GET_COLLECTION_TOP_SUCCESS';
type GET_COLLECTION_TOP_SUCCESS_TYPE = 'ANALYTICS-V2/GET_COLLECTION_TOP_SUCCESS';

export const GET_COLLECTION_TOP_FAILURE = 'ANALYTICS-V2/GET_COLLECTION_TOP_FAILURE';
type GET_COLLECTION_TOP_FAILURE_TYPE = 'ANALYTICS-V2/GET_COLLECTION_TOP_FAILURE';

type GetCollectionTopRequestAction = Action<GET_COLLECTION_TOP_REQUEST_TYPE, null>;
type GetCollectionTopSuccessAction = Action<
  GET_COLLECTION_TOP_SUCCESS_TYPE,
  CollectionAnalyticResponseSuccess
>;
type GetCollectionTopFailureAction = Action<GET_COLLECTION_TOP_FAILURE_TYPE, AnalyticFailure>;

// Collection get total filter click
export const GET_COLLECTION_TOTAL_FILTER_CLICK_REQUEST =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_CLICK_REQUEST';
type GET_COLLECTION_TOTAL_FILTER_CLICK_REQUEST_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_CLICK_REQUEST';

export const GET_COLLECTION_TOTAL_FILTER_CLICK_SUCCESS =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_CLICK_SUCCESS';
type GET_COLLECTION_TOTAL_FILTER_CLICK_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_CLICK_SUCCESS';

export const GET_COLLECTION_TOTAL_FILTER_CLICK_FAILURE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_CLICK_FAILURE';
type GET_COLLECTION_TOTAL_FILTER_CLICK_FAILURE_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_CLICK_FAILURE';

type GetCollectionTotalFilterClickRequestAction = Action<
  GET_COLLECTION_TOTAL_FILTER_CLICK_REQUEST_TYPE,
  null
>;
type GetCollectionTotalFilterClickSuccessAction = Action<
  GET_COLLECTION_TOTAL_FILTER_CLICK_SUCCESS_TYPE,
  CollectionAnalyticResponseSuccess
>;
type GetCollectionTotalFilterClickFailureAction = Action<
  GET_COLLECTION_TOTAL_FILTER_CLICK_FAILURE_TYPE,
  AnalyticFailure
>;

// Collection get total revenue generated by collection
export const GET_COLLECTION_TOTAL_REVENUE_GENERATED_REQUEST =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_REVENUE_GENERATED_REQUEST';
type GET_COLLECTION_TOTAL_REVENUE_GENERATED_REQUEST_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_REVENUE_GENERATED_REQUEST';

export const GET_COLLECTION_TOTAL_REVENUE_GENERATED_SUCCESS =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_REVENUE_GENERATED_SUCCESS';
type GET_COLLECTION_TOTAL_REVENUE_GENERATED_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_REVENUE_GENERATED_SUCCESS';

export const GET_COLLECTION_TOTAL_REVENUE_GENERATED_FAILURE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_REVENUE_GENERATED_FAILURE';
type GET_COLLECTION_TOTAL_REVENUE_GENERATED_FAILURE_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_REVENUE_GENERATED_FAILURE';

type GetCollectionTotalRevenueGeneratedRequestAction = Action<
  GET_COLLECTION_TOTAL_REVENUE_GENERATED_REQUEST_TYPE,
  null
>;
type GetCollectionTotalRevenueGeneratedSuccessAction = Action<
  GET_COLLECTION_TOTAL_REVENUE_GENERATED_SUCCESS_TYPE,
  CollectionAnalyticResponseSuccess
>;
type GetCollectionTotalRevenueGeneratedFailureAction = Action<
  GET_COLLECTION_TOTAL_REVENUE_GENERATED_FAILURE_TYPE,
  AnalyticFailure
>;

// Collection get total filter option value combination
export const GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_REQUEST =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_REQUEST';
type GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_REQUEST_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_REQUEST';

export const GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_SUCCESS =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_SUCCESS';
type GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_SUCCESS';

export const GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_FAILURE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_FAILURE';
type GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_FAILURE_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_FAILURE';

type GetCollectionTotalFilterOptionValueCombinationRequestAction = Action<
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_REQUEST_TYPE,
  null
>;
type GetCollectionTotalFilterOptionValueCombinationSuccessAction = Action<
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_SUCCESS_TYPE,
  CollectionAnalyticResponseSuccess
>;
type GetCollectionTotalFilterOptionValueCombinationFailureAction = Action<
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_COMBINATION_FAILURE_TYPE,
  AnalyticFailure
>;

// Collection get total filter option value
export const GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_REQUEST =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_REQUEST';
type GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_REQUEST';

export const GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_SUCCESS =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_SUCCESS';
type GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_SUCCESS';

export const GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_FAILURE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_FAILURE';
type GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_FAILURE';

type GetCollectionTotalFilterOptionValueRequestAction = Action<
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_REQUEST_TYPE,
  null
>;
type GetCollectionTotalFilterOptionValueSuccessAction = Action<
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_SUCCESS_TYPE,
  CollectionAnalyticResponseSuccess
>;
type GetCollectionTotalFilterOptionValueFailureAction = Action<
  GET_COLLECTION_TOTAL_FILTER_OPTION_VALUE_FAILURE_TYPE,
  AnalyticFailure
>;

// Collection: Top collection performance
export const GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_REQUEST =
  'ANALYTICS-V2/GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_REQUEST';
type GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_REQUEST';

export const GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_SUCCESS =
  'ANALYTICS-V2/GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_SUCCESS';
type GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_SUCCESS';

export const GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_FAILURE =
  'ANALYTICS-V2/GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_FAILURE';
type GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_FAILURE';

type GetCollectionTopCollectionPerformanceRequestAction = Action<
  GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_REQUEST_TYPE,
  null
>;
type GetCollectionTopCollectionPerformanceSuccessAction = Action<
  GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_SUCCESS_TYPE,
  CollectionAnalyticResponseSuccess
>;
type GetCollectionTopCollectionPerformanceFailureAction = Action<
  GET_COLLECTION_TOP_COLLECTION_PERFORMANCE_FAILURE_TYPE,
  AnalyticFailure
>;

// Collection: List collections
export const GET_COLLECTIONS_REQUEST = 'ANALYTICS-V2/GET_COLLECTIONS_REQUEST';
type GET_COLLECTIONS_REQUEST_TYPE = 'ANALYTICS-V2/GET_COLLECTIONS_REQUEST';

export const GET_COLLECTIONS_SUCCESS = 'ANALYTICS-V2/GET_COLLECTIONS_SUCCESS';
type GET_COLLECTIONS_SUCCESS_TYPE = 'ANALYTICS-V2/GET_COLLECTIONS_SUCCESS';

export const GET_COLLECTIONS_FAILURE = 'ANALYTICS-V2/GET_COLLECTIONS_FAILURE';
type GET_COLLECTIONS_FAILURE_TYPE = 'ANALYTICS-V2/GET_COLLECTIONS_FAILURE';

type GetCollectionsRequestAction = Action<GET_COLLECTIONS_REQUEST_TYPE, null>;
type GetCollectionsSuccessAction = Action<
  GET_COLLECTIONS_SUCCESS_TYPE,
  CollectionAnalyticResponseSuccess
>;
type GetCollectionsFailureAction = Action<GET_COLLECTIONS_FAILURE_TYPE, AnalyticFailure>;

// START: Recommendation analytics v2

// Recommendation analytics v2 - total revenue
export const GET_RECOMMENDATION_TOTAL_REVENUE_REQUEST =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOTAL_REVENUE_REQUEST';
type GET_RECOMMENDATION_TOTAL_REVENUE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOTAL_REVENUE_REQUEST';

export const GET_RECOMMENDATION_TOTAL_REVENUE_SUCCESS =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOTAL_REVENUE_SUCCESS';
type GET_RECOMMENDATION_TOTAL_REVENUE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOTAL_REVENUE_SUCCESS';

export const GET_RECOMMENDATION_TOTAL_REVENUE_FAILURE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOTAL_REVENUE_FAILURE';
type GET_RECOMMENDATION_TOTAL_REVENUE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOTAL_REVENUE_FAILURE';

type GetRecommendationTotalRevenueRequestAction = Action<
  GET_RECOMMENDATION_TOTAL_REVENUE_REQUEST_TYPE,
  null
>;
type GetRecommendationTotalRevenueSuccessAction = Action<
  GET_RECOMMENDATION_TOTAL_REVENUE_SUCCESS_TYPE,
  RecommendationAnalyticResponseSuccess
>;
type GetRecommendationTotalRevenueFailureAction = Action<
  GET_RECOMMENDATION_TOTAL_REVENUE_FAILURE_TYPE,
  AnalyticFailure
>;

// Recommendation analytics v2 - conversion rate
export const GET_RECOMMENDATION_CONVERSION_RATE_REQUEST =
  'ANALYTICS-V2/GET_RECOMMENDATION_CONVERSION_RATE_REQUEST';
type GET_RECOMMENDATION_CONVERSION_RATE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_CONVERSION_RATE_REQUEST';

export const GET_RECOMMENDATION_CONVERSION_RATE_SUCCESS =
  'ANALYTICS-V2/GET_RECOMMENDATION_CONVERSION_RATE_SUCCESS';
type GET_RECOMMENDATION_CONVERSION_RATE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_CONVERSION_RATE_SUCCESS';

export const GET_RECOMMENDATION_CONVERSION_RATE_FAILURE =
  'ANALYTICS-V2/GET_RECOMMENDATION_CONVERSION_RATE_FAILURE';
type GET_RECOMMENDATION_CONVERSION_RATE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_CONVERSION_RATE_FAILURE';

type GetRecommendationConversionRateRequestAction = Action<
  GET_RECOMMENDATION_CONVERSION_RATE_REQUEST_TYPE,
  null
>;
type GetRecommendationConversionRateSuccessAction = Action<
  GET_RECOMMENDATION_CONVERSION_RATE_SUCCESS_TYPE,
  RecommendationAnalyticResponseSuccess
>;
type GetRecommendationConversionRateFailureAction = Action<
  GET_RECOMMENDATION_CONVERSION_RATE_FAILURE_TYPE,
  AnalyticFailure
>;

// Recommendation analytics v2 - click rate
export const GET_RECOMMENDATION_CLICK_RATE_REQUEST =
  'ANALYTICS-V2/GET_RECOMMENDATION_CLICK_RATE_REQUEST';
type GET_RECOMMENDATION_CLICK_RATE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_CLICK_RATE_REQUEST';

export const GET_RECOMMENDATION_CLICK_RATE_SUCCESS =
  'ANALYTICS-V2/GET_RECOMMENDATION_CLICK_RATE_SUCCESS';
type GET_RECOMMENDATION_CLICK_RATE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_CLICK_RATE_SUCCESS';

export const GET_RECOMMENDATION_CLICK_RATE_FAILURE =
  'ANALYTICS-V2/GET_RECOMMENDATION_CLICK_RATE_FAILURE';
type GET_RECOMMENDATION_CLICK_RATE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_CLICK_RATE_FAILURE';

type GetRecommendationClickRateRequestAction = Action<
  GET_RECOMMENDATION_CLICK_RATE_REQUEST_TYPE,
  null
>;
type GetRecommendationClickRateSuccessAction = Action<
  GET_RECOMMENDATION_CLICK_RATE_SUCCESS_TYPE,
  RecommendationAnalyticResponseSuccess
>;
type GetRecommendationClickRateFailureAction = Action<
  GET_RECOMMENDATION_CLICK_RATE_FAILURE_TYPE,
  AnalyticFailure
>;

// Recommendation analytics v2 - top page performance
export const GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_REQUEST =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_REQUEST';
type GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_REQUEST';

export const GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_SUCCESS =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_SUCCESS';
type GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_SUCCESS';

export const GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_FAILURE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_FAILURE';
type GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_FAILURE';

type GetRecommendationTopPagePerformanceRequestAction = Action<
  GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_REQUEST_TYPE,
  null
>;
type GetRecommendationTopPagePerformanceSuccessAction = Action<
  GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_SUCCESS_TYPE,
  RecommendationAnalyticResponseSuccess
>;
type GetRecommendationTopPagePerformanceFailureAction = Action<
  GET_RECOMMENDATION_TOP_PAGE_PERFORMANCE_FAILURE_TYPE,
  AnalyticFailure
>;

// Recommendation analytics v2 - top widget performance
export const GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_REQUEST =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_REQUEST';
type GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_REQUEST';

export const GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_SUCCESS =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_SUCCESS';
type GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_SUCCESS';

export const GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_FAILURE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_FAILURE';
type GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_FAILURE';

type GetRecommendationTopWidgetPerformanceRequestAction = Action<
  GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_REQUEST_TYPE,
  null
>;
type GetRecommendationTopWidgetPerformanceSuccessAction = Action<
  GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_SUCCESS_TYPE,
  RecommendationAnalyticResponseSuccess
>;
type GetRecommendationTopWidgetPerformanceFailureAction = Action<
  GET_RECOMMENDATION_TOP_WIDGET_PERFORMANCE_FAILURE_TYPE,
  AnalyticFailure
>;

// Recommendation analytics v2 - top product performance
export const GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_REQUEST =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_REQUEST';
type GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_REQUEST_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_REQUEST';

export const GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_SUCCESS =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_SUCCESS';
type GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_SUCCESS_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_SUCCESS';

export const GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_FAILURE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_FAILURE';
type GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_FAILURE_TYPE =
  'ANALYTICS-V2/GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_FAILURE';

type GetRecommendationTopProductPerformanceRequestAction = Action<
  GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_REQUEST_TYPE,
  null
>;
type GetRecommendationTopProductPerformanceSuccessAction = Action<
  GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_SUCCESS_TYPE,
  RecommendationAnalyticResponseSuccess
>;
type GetRecommendationTopProductPerformanceFailureAction = Action<
  GET_RECOMMENDATION_TOP_PRODUCT_PERFORMANCE_FAILURE_TYPE,
  AnalyticFailure
>;

// END: Recommendation analytics v2

// START: Pre-order analytics v2

// Pre-order analytics v2 - top product performance
export const GET_PRE_ORDER_TOP_PRODUCT_REQUEST = 'ANALYTICS-V2/GET_PRE_ORDER_TOP_PRODUCT_REQUEST';
type GET_PRE_ORDER_TOP_PRODUCT_REQUEST_TYPE = 'ANALYTICS-V2/GET_PRE_ORDER_TOP_PRODUCT_REQUEST';

export const GET_PRE_ORDER_TOP_PRODUCT_SUCCESS = 'ANALYTICS-V2/GET_PRE_ORDER_TOP_PRODUCT_SUCCESS';
type GET_PRE_ORDER_TOP_PRODUCT_SUCCESS_TYPE = 'ANALYTICS-V2/GET_PRE_ORDER_TOP_PRODUCT_SUCCESS';

export const GET_PRE_ORDER_TOP_PRODUCT_FAILURE = 'ANALYTICS-V2/GET_PRE_ORDER_TOP_PRODUCT_FAILURE';
type GET_PRE_ORDER_TOP_PRODUCT_FAILURE_TYPE = 'ANALYTICS-V2/GET_PRE_ORDER_TOP_PRODUCT_FAILURE';

type GetPreOrderTopProductRequestAction = Action<GET_PRE_ORDER_TOP_PRODUCT_REQUEST_TYPE, null>;
type GetPreOrderTopProductSuccessAction = Action<
  GET_PRE_ORDER_TOP_PRODUCT_SUCCESS_TYPE,
  RecommendationAnalyticResponseSuccess
>;
type GetPreOrderTopProductFailureAction = Action<
  GET_PRE_ORDER_TOP_PRODUCT_FAILURE_TYPE,
  AnalyticFailure
>;
// END: Pre-order analytics v2

// START: Setting
export const GET_ANALYTICS_SETTINGS_REQUEST = 'GET_ANALYTICS_SETTINGS_REQUEST';
type GET_ANALYTICS_SETTINGS_REQUEST_TYPE = 'GET_ANALYTICS_SETTINGS_REQUEST';

export const GET_ANALYTICS_SETTINGS_SUCCESS = 'GET_ANALYTICS_SETTINGS_SUCCESS';
type GET_ANALYTICS_SETTINGS_SUCCESS_TYPE = 'GET_ANALYTICS_SETTINGS_SUCCESS';

export const GET_ANALYTICS_SETTINGS_FAILURE = 'GET_ANALYTICS_SETTINGS_FAILURE';
type GET_ANALYTICS_SETTINGS_FAILURE_TYPE = 'GET_ANALYTICS_SETTINGS_FAILURE';

export const UPDATE_ANALYTICS_SETTINGS_REQUEST = 'UPDATE_ANALYTICS_SETTINGS_REQUEST';
type UPDATE_ANALYTICS_SETTINGS_REQUEST_TYPE = 'UPDATE_ANALYTICS_SETTINGS_REQUEST';

export const UPDATE_ANALYTICS_SETTINGS_SUCCESS = 'UPDATE_ANALYTICS_SETTINGS_SUCCESS';
type UPDATE_ANALYTICS_SETTINGS_SUCCESS_TYPE = 'UPDATE_ANALYTICS_SETTINGS_SUCCESS';

export const UPDATE_ANALYTICS_SETTINGS_FAILURE = 'UPDATE_ANALYTICS_SETTINGS_FAILURE';
type UPDATE_ANALYTICS_SETTINGS_FAILURE_TYPE = 'UPDATE_ANALYTICS_SETTINGS_FAILURE';

type GetAnalyticsSettingsRequestAction = Action<GET_ANALYTICS_SETTINGS_REQUEST_TYPE, null>;

type GetAnalyticsSettingsSuccessAction = Action<
  GET_ANALYTICS_SETTINGS_SUCCESS_TYPE,
  AnalyticsSettingsSuccess
>;

type GetAnalyticsSettingsFailureAction = Action<
  GET_ANALYTICS_SETTINGS_FAILURE_TYPE,
  AnalyticFailure
>;

type UpdateAnalyticsSettingsRequestAction = Action<UPDATE_ANALYTICS_SETTINGS_REQUEST_TYPE, null>;

type UpdateAnalyticsSettingsSuccessAction = Action<
  UPDATE_ANALYTICS_SETTINGS_SUCCESS_TYPE,
  AnalyticsSettingsSuccess
>;

type UpdateAnalyticsSettingsFailureAction = Action<
  UPDATE_ANALYTICS_SETTINGS_FAILURE_TYPE,
  AnalyticFailure
>;

// END: Setting

// Action and State
export type AnalyticsAction =
  | GetTopSearchTermsWithClickRequestAction
  | GetTopSearchTermsWithClickSuccessAction
  | GetTopSearchTermsWithClickFailureAction
  | ChangeValueAction
  | SortDataAction
  | GetTotalSearchRevenueRequestAction
  | GetTotalSearchRevenueSuccessAction
  | GetTotalSearchRevenueFailureAction
  | GetTopSearchTermsRequestAction
  | GetTopSearchTermsSuccessAction
  | GetTopSearchTermsFailureAction
  | GetTopSearchTermDetailRequestAction
  | GetTopSearchTermDetailSuccessAction
  | GetTopSearchTermDetailFailureAction
  | GetTopSearchProductPerformanceRequestAction
  | GetTopSearchProductPerformanceSuccessAction
  | GetTopSearchProductPerformanceFailureAction
  | GetSearchTermsWithoutResultRequestAction
  | GetSearchTermsWithoutResultSuccessAction
  | GetSearchTermsWithoutResultFailureAction
  | GetSearchesWithoutResultRateRequestAction
  | GetSearchesWithoutResultRateSuccessAction
  | GetSearchesWithoutResultRateFailureAction
  | GetSearchesClickRateRequestAction
  | GetSearchesClickRateSuccessAction
  | GetSearchesClickRateFailureAction
  | GetSearchesWithoutClickRateRequestAction
  | GetSearchesWithoutClickRateSuccessAction
  | GetSearchesWithoutClickRateFailureAction
  | GetSearchTermsWithoutClickRequestAction
  | GetSearchTermsWithoutClickSuccessAction
  | GetSearchTermsWithoutClickFailureAction
  | GetConversionRateRequestAction
  | GetConversionRateSuccessAction
  | GetConversionRateFailureAction
  | GetSearchesRateRequestAction
  | GetSearchesRateSuccessAction
  | GetSearchesRateFailureAction
  | GetSearchTermsSearchesRequestAction
  | GetSearchTermsSearchesSuccessAction
  | GetSearchTermsSearchesFailureAction
  | ExportDataRequestAction
  | ExportDataSuccessAction
  | ExportDataFailureAction
  | getSearchesWithoutClickAction
  | getSearchesWithoutClickSuccessAction
  | getSearchesWithoutClickFailureAction
  | GetTotalActivityRequestAction
  | GetTotalActivitySuccessAction
  | GetTotalActivityFailureAction
  | GetTotalRevenueRequestAction
  | GetTotalRevenueSuccessAction
  | GetTotalRevenueFailureAction
  | GetAppImpactConversionRateRequestAction
  | GetAppImpactConversionRateSuccessAction
  | GetAppImpactConversionRateFailureAction
  | GetOrderCountRequestAction
  | GetOrderCountSuccessAction
  | GetOrderCountFailureAction
  | GetAverageOrderValueRequestAction
  | GetAverageOrderValueSuccessAction
  | GetAverageOrderValueFailureAction
  | GetRevenuePerVisitorRequestAction
  | GetRevenuePerVisitorSuccessAction
  | GetRevenuePerVisitorFailureAction
  | GetCollectionConversionRateRequestAction
  | GetCollectionConversionRateSuccessAction
  | GetCollectionConversionRateFailureAction
  | GetCollectionTopRequestAction
  | GetCollectionTopSuccessAction
  | GetCollectionTopFailureAction
  | GetCollectionTotalFilterClickRequestAction
  | GetCollectionTotalFilterClickSuccessAction
  | GetCollectionTotalFilterClickFailureAction
  | GetCollectionTotalRevenueGeneratedRequestAction
  | GetCollectionTotalRevenueGeneratedSuccessAction
  | GetCollectionTotalRevenueGeneratedFailureAction
  | GetCollectionTotalFilterOptionValueCombinationRequestAction
  | GetCollectionTotalFilterOptionValueCombinationSuccessAction
  | GetCollectionTotalFilterOptionValueCombinationFailureAction
  | GetCollectionTotalFilterOptionValueRequestAction
  | GetCollectionTotalFilterOptionValueSuccessAction
  | GetCollectionTotalFilterOptionValueFailureAction
  | GetCollectionTopCollectionPerformanceRequestAction
  | GetCollectionTopCollectionPerformanceSuccessAction
  | GetCollectionTopCollectionPerformanceFailureAction
  | GetCollectionsRequestAction
  | GetCollectionsSuccessAction
  | GetCollectionsFailureAction
  | GetRecommendationTotalRevenueRequestAction
  | GetRecommendationTotalRevenueSuccessAction
  | GetRecommendationTotalRevenueFailureAction
  | GetRecommendationConversionRateRequestAction
  | GetRecommendationConversionRateSuccessAction
  | GetRecommendationConversionRateFailureAction
  | GetRecommendationClickRateRequestAction
  | GetRecommendationClickRateSuccessAction
  | GetRecommendationClickRateFailureAction
  | GetRecommendationTopPagePerformanceRequestAction
  | GetRecommendationTopPagePerformanceSuccessAction
  | GetRecommendationTopPagePerformanceFailureAction
  | GetRecommendationTopWidgetPerformanceRequestAction
  | GetRecommendationTopWidgetPerformanceSuccessAction
  | GetRecommendationTopWidgetPerformanceFailureAction
  | GetRecommendationTopProductPerformanceRequestAction
  | GetRecommendationTopProductPerformanceSuccessAction
  | GetRecommendationTopProductPerformanceFailureAction
  | GetAnalyticsSettingsRequestAction
  | GetAnalyticsSettingsSuccessAction
  | GetAnalyticsSettingsFailureAction
  | GetPreOrderTopProductRequestAction
  | GetPreOrderTopProductSuccessAction
  | GetPreOrderTopProductFailureAction
  | UpdateAnalyticsSettingsRequestAction
  | UpdateAnalyticsSettingsSuccessAction
  | UpdateAnalyticsSettingsFailureAction;

export type AnalyticsState = {
  loading: boolean,
  exporting: boolean,
  success: boolean,
  error: any,

  // Filter
  // Search analytics
  searchAnalyticsFromDate: Date,
  searchAnalyticsToDate: Date,
  searchAnalyticsCompare: boolean,

  // Top search terms without click
  topSearchTermsWithoutClickLoading: boolean,
  topSearchTermsWithoutClickSuccess: boolean,
  topSearchTermsWithoutClick: Array<Object>,
  topSearchTermsWithoutClickExecutionId: string,

  // Searches
  // Top search term
  topSearchTermsLoading: boolean,
  topSearchTermsSuccess: boolean,
  topSearchTerms: SearchTermsType,
  topSearchTermsQueryExecutionId: string,

  // Top search terms with click
  topSearchTermsWithClickLoading: boolean,
  topSearchTermsWithClickSuccess: boolean,
  topSearchTermsWithClick: SearchTermsType,
  topSearchTermsWithClickExecutionId: string,

  // Top search term detail
  topSearchTermDetailLoading: boolean,
  topSearchTermDetailSuccess: boolean,
  topSearchTermDetail: SearchTermsType,
  topSearchTermDetailQueryExecutionId: string,

  // Searches without result rate
  searchesWithoutResultRate: TimeSeriesType,
  searchesWithoutResultRateCompared: TimeSeriesType,
  searchesWithoutResultRateLoading: boolean,
  searchesWithoutResultRateSuccess: boolean,
  searchesWithoutResultRateQueryExecutionId: string,
  searchesWithoutResultRateComparedQueryExecutionId: string,
  searchesWithoutResultRatePercent: string,
  searchesWithoutResultRatePercentChange: number,

  // Search terms without result
  searchTermsWithoutResult: SearchTermsType,
  searchTermsWithoutResultLoading: boolean,
  searchTermsWithoutResultSuccess: boolean,
  searchTermsWithoutResultQueryExecutionId: string,
  searchTermsWithoutResultTotal: number,

  // Searches click rate
  searchRevenue: TimeSeriesType,
  searchRevenueCompared: TimeSeriesType,
  searchRevenueLoading: boolean,
  searchRevenueSuccess: boolean,
  searchRevenueTotal: number,
  searchRevenuePercentChange: number | null,
  searchRevenueExecutionId: string,
  searchRevenueComparedExecutionId: string,

  // Searches click rate
  searchesClickRate: TimeSeriesType,
  searchesClickRateCompared: TimeSeriesType,
  searchesClickRateLoading: boolean,
  searchesClickRateSuccess: boolean,
  searchesClickRatePercent: string,
  searchesClickRatePercentChange: number | null,
  searchesClickRateExecutionId: string,
  searchesClickRateComparedExecutionId: string,

  // Total searches without click
  totalSearchesWithoutClick: TimeSeriesType,
  totalSearchesWithoutClickCompared: TimeSeriesType,
  totalSearchesWithoutClickLoading: boolean,
  totalSearchesWithoutClickSuccess: boolean,
  totalSearchesWithoutClickPercent: string,
  totalSearchesWithoutClickPercentChange: number | null,
  totalSearchesWithoutClickExecutionId: string,
  totalSearchesWithoutClickComparedExecutionId: string,

  // Search terms without click
  searchTermsWithoutClick: SearchTermsType,
  searchTermsWithoutClickLoading: boolean,
  searchTermsWithoutClickSuccess: boolean,
  searchTermsWithoutClickTotal: number,

  // Conversion rate
  conversionRate: ConversionRateType,
  conversionRateCompared: ConversionRateType,
  conversionRatePercent: string,
  conversionRatePercentChange: number | null,
  conversionRateLoading: boolean,
  conversionRateSuccess: boolean,
  conversionRateQueryExecutionId: string,
  conversionRateComparedExecutionId: string,

  // Total searches
  totalSearchesLoading: boolean,
  totalSearchesSuccess: boolean,
  totalSearches: TimeSeriesType,
  totalSearchesCompared: TimeSeriesType,
  totalSearchesNumber: string,
  totalSearchesPercentChange: number,
  totalSearchesQueryExecutionId: string,
  totalSearchesComparedExecutionId: string,

  // Search term searches
  searchTermsSearchesLoading: boolean,
  searchTermsSearchesSuccess: boolean,
  searchTermsSearches: SearchTermsType,
  searchTermsSearchesQueryExecutionId: string,
  searchTermsSearchesTotal: number,

  // Top products on search
  topProductsOnSearchLoading: boolean,
  topProductsOnSearchSuccess: boolean,
  topProductsOnSearch: TopProductsOnSearchType,
  topProductsOnSearchExecutionId: string,

  // App impact
  appImpactFromDate: Date,
  appImpactToDate: Date,
  appImpactCompare: boolean,

  // Total revenue
  totalRevenueLoading: boolean,
  totalRevenueSuccess: boolean,
  totalRevenueData: ChartSourceType,
  totalRevenueCompared: ChartSourceType,
  totalRevenueTotal: AppImpactSpecific,
  totalRevenuePercentChange: AppImpactSpecific,
  totalRevenueQueryExecutionId: string,
  totalRevenueComparedQueryExecutionId: string,

  // Order count
  orderCountLoading: boolean,
  orderCountSuccess: boolean,
  orderCountData: ChartSourceType,
  orderCountCompared: ChartSourceType,
  orderCountTotal: AppImpactSpecific,
  orderCountPercentChange: AppImpactSpecific,
  orderCountQueryExecutionId: string,
  orderCountComparedQueryExecutionId: string,

  // Average order value
  averageOrderValueLoading: boolean,
  averageOrderValueSuccess: boolean,
  averageOrderValueData: ChartSourceType,
  averageOrderValueCompared: ChartSourceType,
  averageOrderValueTotal: AppImpactSpecific,
  averageOrderValuePercentChange: AppImpactSpecific,
  averageOrderValueQueryExecutionId: string,
  averageOrderValueComparedQueryExecutionId: string,

  // Revenue per visitor
  revenuePerVisitorLoading: boolean,
  revenuePerVisitorSuccess: boolean,
  revenuePerVisitorData: ChartSourceType,
  revenuePerVisitorCompared: ChartSourceType,
  revenuePerVisitorTotal: AppImpactSpecific,
  revenuePerVisitorPercentChange: AppImpactSpecific,
  revenuePerVisitorQueryExecutionId: string,
  revenuePerVisitorComparedQueryExecutionId: string,

  //=======================

  // Collection Analytic
  collectionAnalyticFromDate: Date,
  collectionAnalyticToDate: Date,
  collectionAnalyticCompare: boolean,

  // Collection: total revenue generated (chart)
  collectionTotalRevenueGenerated: ChartSourceType,
  collectionTotalRevenueGeneratedCompared: ChartSourceType,
  collectionTotalRevenueGeneratedPercentChange: AppImpactSpecific,
  collectionTotalRevenueGeneratedTotal: string,
  collectionTotalRevenueGeneratedLoading: boolean,
  collectionTotalRevenueGeneratedSuccess: boolean,
  collectionTotalRevenueGeneratedQueryExecutionId: string,
  collectionTotalRevenueGeneratedComparedQueryExecutionId: string,

  // Collection: total filter click (chart)
  collectionTotalFilterClick: ChartSourceType,
  collectionTotalFilterClickCompared: ChartSourceType,
  collectionTotalFilterClickPercentChange: AppImpactSpecific,
  collectionTotalFilterClickTotal: string,
  collectionTotalFilterClickLoading: boolean,
  collectionTotalFilterClickSuccess: boolean,
  collectionTotalFilterClickQueryExecutionId: string,
  collectionTotalFilterClickComparedQueryExecutionId: string,

  // Collection: conversion rate (chart)
  collectionConversionRate: CollectionConversionRatesType,
  collectionConversionRateCompared: CollectionConversionRatesType,
  collectionConversionRatePercentChange: AppImpactSpecific,
  collectionConversionRatePercent: string,
  collectionConversionRateLoading: boolean,
  collectionConversionRateSuccess: boolean,
  collectionConversionRateQueryExecutionId: string,
  collectionConversionRateComparedQueryExecutionId: string,

  // Collection: top
  collectionTopLoading: boolean,
  collectionTopSuccess: boolean,
  collectionTop: CollectionTopsType,
  collectionTopQueryExecutionId: string,
  collectionTopTotal: 0,

  // Collection: top filter option value
  collectionTopFilterOptionValueLoading: boolean,
  collectionTopFilterOptionValueSuccess: boolean,
  collectionTopFilterOptionValue: CollectionTopsType,
  collectionTopFilterOptionValueQueryExecutionId: string,
  collectionTopFilterOptionValueTotal: 0,

  // Collection: top filter option value combination
  collectionTopFilterOptionValueCombinationLoading: boolean,
  collectionTopFilterOptionValueCombinationSuccess: boolean,
  collectionTopFilterOptionValueCombination: CollectionTopFilterOptionValueCombinationsType,
  collectionTopFilterOptionValueCombinationQueryExecutionId: string,
  collectionTopFilterOptionValueCombinationTotal: 0,

  // Collection: List collections
  collectionsLoading: boolean,
  collectionsSuccess: boolean,
  collections: [],
  collectionsQueryExecutionId: string,

  // Recommendation: total revenue generated (chart)
  recommendationTotalRevenue: ChartSourceType,
  recommendationTotalRevenueCompared: ChartSourceType,
  recommendationTotalRevenuePercentChange: AppImpactSpecific,
  recommendationTotalRevenueTotal: string,
  recommendationTotalRevenueLoading: boolean,
  recommendationTotalRevenueSuccess: boolean,
  recommendationTotalRevenueQueryExecutionId: string,
  recommendationTotalRevenueComparedQueryExecutionId: string,

  // Recommendation: conversion rate (chart)
  recommendationConversionRate: RecommendationConversionRatesType,
  recommendationConversionRateCompared: RecommendationConversionRatesType,
  recommendationConversionRatePercentChange: AppImpactSpecific,
  recommendationConversionRatePercent: string,
  recommendationConversionRateLoading: boolean,
  recommendationConversionRateSuccess: boolean,
  recommendationConversionRateQueryExecutionId: string,
  recommendationConversionRateComparedQueryExecutionId: string,

  // Recommendation: click rate (chart)
  recommendationClickRate: RecommendationClickRatesType,
  recommendationClickRateCompared: RecommendationClickRatesType,
  recommendationClickRatePercentChange: AppImpactSpecific,
  recommendationClickRatePercent: string,
  recommendationClickRateLoading: boolean,
  recommendationClickRateSuccess: boolean,
  recommendationClickRateQueryExecutionId: string,
  recommendationClickRateComparedQueryExecutionId: string,

  // Recommendation: top page performance
  recommendationTopPagePerformanceLoading: boolean,
  recommendationTopPagePerformanceSuccess: boolean,
  recommendationTopPagePerformance: RecommendationTopsType,
  recommendationTopPagePerformanceQueryExecutionId: string,
  recommendationTopPagePerformanceTotal: 0,

  // Recommendation: top widget performance
  recommendationTopWidgetPerformanceLoading: boolean,
  recommendationTopWidgetPerformanceSuccess: boolean,
  recommendationTopWidgetPerformance: RecommendationTopsType,
  recommendationTopWidgetPerformanceQueryExecutionId: string,
  recommendationTopWidgetPerformanceTotal: 0,

  // Recommendation: top product performance
  recommendationTopProductPerformanceLoading: boolean,
  recommendationTopProductPerformanceSuccess: boolean,
  recommendationTopProductPerformance: RecommendationTopProductPerformancesType,
  recommendationTopProductPerformanceQueryExecutionId: string,
  recommendationTopProductPerformanceTotal: 0,

  // Pre-Order: top product
  preOrderTopProductLoading: boolean,
  preOrderTopProductSuccess: boolean,
  preOrderTopProduct: PreOrderTopProductType,
  preOrderTopProductQueryExecutionId: string,
  preOrderTopProductTotal: 0
};
